import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Briefcase, DollarSign, BarChart3, Receipt, Save, Loader2, SlidersHorizontal, BellRing, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useCompanyDefaults, saveCompanyDefaults } from '../hooks/useCompanyDefaults';
import { CATS } from '../pages/ReportsEnhanced';

const API = `${window.__BACKEND_URL__}/api`;
const RANGES = [
  ['this_pay_period', 'This pay period'], ['last_pay_period', 'Last pay period'], ['this_week', 'This week'],
  ['this_month', 'This month'], ['last_month', 'Last month'], ['last_3_months', 'Last 3 months'],
];
const parseSteps = (v) => Array.from(new Set(String(v).split(',').map((x) => x.trim()).filter((x) => x !== '' && !Number.isNaN(Number(x))).map(Number))).sort((a, b) => a - b);
const selectClass = 'w-full h-9 rounded-md px-2 text-sm bg-[#0B1321] border border-[#2D3748] text-white';
const labelStyle = { color: '#94A3B8' };

const Section = ({ icon: Icon, title, blurb, children, testId }) => (
  <div className="rounded-xl p-5" style={{ background: '#0F1923', border: '1px solid #2D3748' }} data-testid={testId}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4" style={{ color: '#1E88E5' }} />
      <h3 className="text-sm font-semibold text-white">{title}</h3>
    </div>
    <p className="text-xs mb-4" style={labelStyle}>{blurb}</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium mb-1" style={labelStyle}>{label}</span>
    {children}
  </label>
);

const Toggle = ({ checked, onChange, label, testId }) => (
  <label className="flex items-center gap-2 text-sm text-white cursor-pointer mt-5">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-blue-500" data-testid={testId} />
    {label}
  </label>
);

// Settings → Company Defaults: company-wide behaviour for staff screens.
export default function CompanyDefaultsCard({ token }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { defaults, loaded } = useCompanyDefaults(token);
  const [form, setForm] = useState(defaults);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (loaded) setForm(defaults); }, [loaded, defaults]);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/company/document-presets`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setTemplates(r.data.presets || [])).catch(() => {});
  }, [token]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const [stepsDraft, setStepsDraft] = useState((defaults.payment_reminders_steps || []).join(', '));
  useEffect(() => { setStepsDraft((form.payment_reminders_steps || []).join(', ')); }, [form.payment_reminders_steps]);
  const dirty = JSON.stringify(form) !== JSON.stringify(defaults);

  const save = async () => {
    setSaving(true);
    try {
      await saveCompanyDefaults(token, { ...form, payment_reminders_steps: parseSteps(stepsDraft) });
      toast({ title: 'Company defaults saved', description: 'Staff will see the new defaults the next time they open those pages.' });
    } catch (e) {
      toast({ title: 'Could not save', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="company-defaults">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5" style={{ color: '#1E88E5' }} />
            <h2 className="text-lg font-semibold text-white">Company Defaults</h2>
          </div>
          <p className="text-sm mt-1" style={labelStyle}>
            How HinesTime behaves for everyone in your company. Individual staff can still change some views on their own device.
          </p>
        </div>
        <button
          type="button" onClick={save} disabled={saving || !dirty}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
          style={{ background: '#1E88E5', color: '#fff' }} data-testid="company-defaults-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>

      <Section icon={Clock} title="Clock-in" blurb="A project is always required at clock-in. Choose whether workers may pick the catch-all “General Work” project or must pick a real project (and optionally a job under it)." testId="company-defaults-clockin">
        <div className="md:col-span-2 flex items-start justify-between gap-4 rounded-lg p-3" style={{ background: '#0B1321', border: '1px solid #2D3748' }}>
          <div>
            <p className="text-sm font-medium text-white">Require a real project — no “General Work” clock-ins</p>
            <p className="text-xs mt-0.5" style={labelStyle}>
              {form.clockin_require_real_project
                ? 'ON — General Work is hidden from every clock-in picker and rejected by the server for worker and supervisor punches.'
                : 'OFF — workers can clock into General Work for genuine non-project time (default).'}
              {' '}Kiosk PIN, offline and QR punches (no picker) still land on General Work so no punch is ever project-less.
            </p>
          </div>
          <button type="button" role="switch" aria-checked={!!form.clockin_require_real_project}
            onClick={() => set('clockin_require_real_project')(!form.clockin_require_real_project)}
            className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors"
            style={{ background: form.clockin_require_real_project ? '#00C896' : '#2D3748' }} data-testid="cd-clockin-require-project">
            <span className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" style={{ transform: form.clockin_require_real_project ? 'translateX(22px)' : 'translateX(2px)' }} />
          </button>
        </div>
      </Section>

      <Section icon={Briefcase} title="Jobs" blurb="What workers and supervisors see when they open Jobs or the Jobs Board." testId="company-defaults-jobs">
        <Field label="Job list opens on">
          <select value={form.jobs_default_scope} onChange={(e) => set('jobs_default_scope')(e.target.value)} className={selectClass} data-testid="cd-jobs-scope">
            <option value="mine">My jobs (assigned directly to the worker)</option>
            <option value="team">My team's jobs (plus jobs assigned to their team)</option>
          </select>
        </Field>
        <Toggle checked={form.jobs_show_team_badge} onChange={set('jobs_show_team_badge')} label='Show a "Team" chip on jobs that reached a worker through their team' testId="cd-jobs-team-badge" />
      </Section>

      <Section icon={DollarSign} title="Payroll" blurb="Payroll Report workbench behaviour for admins and supervisors." testId="company-defaults-payroll">
        <Field label="Workers per page">
          <select value={form.payroll_page_size} onChange={(e) => set('payroll_page_size')(Number(e.target.value))} className={selectClass} data-testid="cd-payroll-page-size">
            {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>
        <Toggle checked={form.payroll_remember_view} onChange={set('payroll_remember_view')} label="Remember each admin's page, search, date range and expanded workers" testId="cd-payroll-remember" />
      </Section>

      <Section icon={BarChart3} title="Reports" blurb="Where Reports & Analytics lands when a manager opens it." testId="company-defaults-reports">
        <Field label="Default report">
          <select value={form.reports_default_report} onChange={(e) => set('reports_default_report')(e.target.value)} className={selectClass} data-testid="cd-reports-report">
            {CATS.map((c) => (
              <optgroup key={c.label} label={c.label}>
                {c.items.map((it) => <option key={it.key} value={it.key}>{it.title}</option>)}
              </optgroup>
            ))}
          </select>
        </Field>
        <Field label="Default date range">
          <select value={form.reports_default_range} onChange={(e) => set('reports_default_range')(e.target.value)} className={selectClass} data-testid="cd-reports-range">
            {RANGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
      </Section>

      <Section icon={Receipt} title="Invoices" blurb="Numbering continues in sequence from the starting number you set — no automatic resets." testId="company-defaults-invoices">
        <Field label="Default Daily Site Invoice template">
          <select value={form.invoices_default_template_id || ''} onChange={(e) => set('invoices_default_template_id')(e.target.value || null)} className={selectClass} data-testid="cd-invoices-template">
            <option value="">None — company defaults</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <div className="flex flex-wrap items-end gap-2 text-xs">
          <button type="button" onClick={() => navigate('/settings?section=invoice-template')} className="underline" style={{ color: '#1E88E5' }} data-testid="cd-link-invoice-template">Invoice numbering & template</button>
          <span style={labelStyle}>·</span>
          <button type="button" onClick={() => navigate('/settings?section=branding')} className="underline" style={{ color: '#1E88E5' }} data-testid="cd-link-document-style">Document Style & templates</button>
        </div>
      </Section>

      <Section icon={BellRing} title="Payment reminders" blurb="Turn past-due reminders on for every customer that has an email — one switch instead of one customer at a time. A customer whose reminders were switched off individually stays off." testId="company-defaults-reminders">
        <Toggle checked={!!form.payment_reminders_all_customers} onChange={set('payment_reminders_all_customers')} label="Send automatic payment reminders to every customer with an email" testId="cd-reminders-all" />
        <Field label="Reminder steps (days after the due date)">
          <input value={stepsDraft} onChange={(e) => setStepsDraft(e.target.value)} onBlur={() => set('payment_reminders_steps')(parseSteps(stepsDraft))} placeholder="3, 14, 30" className={selectClass} data-testid="cd-reminders-steps" />
        </Field>
        <p className="text-xs" style={labelStyle}>Reminders attach a branded statement and are sent once per invoice per step. Per-customer steps and recipients (Settings → Customers) override these.</p>
      </Section>
    </div>
  );
}
